import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Download, RotateCw, Upload, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button, Card, Checkbox, ErrorBox, Field, Input, Spinner, Textarea } from "../components/kd";
import { fmtDate } from "../lib/format";

const ST: Record<string, string> = { OFFEN: "Offen", ANGENOMMEN: "Angenommen", ABGELAUFEN: "Abgelaufen", ZURUECKGEZOGEN: "Zurückgezogen" };

function parseCsv(text: string): any[] {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const sep = lines[0].includes(";") ? ";" : ",";
  const head = lines[0].split(sep).map((h) => h.trim().replace(/^"|"$/g, ""));
  return lines.slice(1).map((l) => Object.fromEntries(l.split(sep).map((v, i) => [head[i] || `s${i}`, v.trim().replace(/^"|"$/g, "")])));
}

function Single({ reload }: { reload: () => void }) {
  const [b, setB] = useState({ name: "", praxisname: "", email: "", nachricht: "" });
  const [err, setErr] = useState("");
  const send = async () => { setErr(""); try { await api.post("/praxis/einladungen", { ...b, nachricht: b.nachricht || null }); toast.success("Einladung versendet."); setB({ name: "", praxisname: "", email: "", nachricht: "" }); reload(); } catch (e) { setErr(errMsg(e)); } };
  return (
    <Card><h2 className="mb-4">Einzeln einladen</h2><ErrorBox>{err}</ErrorBox>
      <Field label="Name" htmlFor="in"><Input id="in" value={b.name} onChange={(e) => setB({ ...b, name: e.target.value })} data-testid="inv-name-input" /></Field>
      <Field label="Praxisname" htmlFor="ip"><Input id="ip" value={b.praxisname} onChange={(e) => setB({ ...b, praxisname: e.target.value })} data-testid="inv-praxis-input" /></Field>
      <Field label="E-Mail" required htmlFor="ie"><Input id="ie" type="email" value={b.email} onChange={(e) => setB({ ...b, email: e.target.value })} data-testid="inv-email-input" /></Field>
      <Field label="Persönliche Nachricht (optional)" htmlFor="im"><Textarea id="im" rows={3} value={b.nachricht} onChange={(e) => setB({ ...b, nachricht: e.target.value })} data-testid="inv-message-input" /></Field>
      <Button onClick={send} data-testid="inv-send-button">Einladung senden</Button>
    </Card>
  );
}

function CsvUpload({ reload }: { reload: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<any[] | null>(null);
  const [raw, setRaw] = useState<any[]>([]);
  const [err, setErr] = useState("");
  const load = async (f?: File) => {
    if (!f) return;
    setErr("");
    const data = parseCsv(await f.text());
    if (data.length > 200) return setErr("Maximal 200 Einträge pro Upload.");
    setRaw(data);
    try { setRows((await api.post("/praxis/einladungen/csv/pruefen", { zeilen: data })).data); } catch (e) { setErr(errMsg(e)); }
  };
  const send = async () => {
    try { const r = await api.post("/praxis/einladungen/csv", { zeilen: raw }); toast.success(`${r.data.versendet} Einladungen versendet.`); setRows(r.data.fehler.length ? r.data.fehler : null); reload(); } catch (e) { setErr(errMsg(e)); }
  };
  const ok = rows?.filter((r) => !r.fehler).length || 0;
  return (
    <Card><h2 className="mb-2">Mehrere per CSV</h2><p className="mb-3 text-sm text-text-muted">Spalten: Name, Praxis, E-Mail (Trennzeichen ; oder ,). Max. 200 Einträge.</p>
      <ErrorBox>{err}</ErrorBox>
      <input ref={ref} type="file" accept=".csv,text/csv" hidden onChange={(e) => { load(e.target.files?.[0]); e.target.value = ""; }} data-testid="csv-file-input" />
      <Button variant="secondary" onClick={() => ref.current?.click()} data-testid="csv-upload-button"><Upload className="h-4 w-4" aria-hidden />CSV auswählen</Button>
      {rows && (
        <div className="mt-4" data-testid="csv-preview">
          <p className="mb-2 text-sm">{ok} gültig · {rows.length - ok} mit Fehler</p>
          <div className="max-h-72 overflow-auto rounded-lg border border-border"><table className="w-full text-left text-sm">
            <tbody>{rows.map((r) => <tr key={r.zeile} className="border-b border-border last:border-0"><td className="px-2 py-1">{r.zeile}</td><td className="px-2 py-1">{r.name}</td><td className="px-2 py-1">{r.praxis}</td><td className="px-2 py-1">{r.email}</td><td className="px-2 py-1 text-danger">{r.fehler || ""}</td></tr>)}</tbody>
          </table></div>
          <Button className="mt-3" disabled={!ok} onClick={send} data-testid="csv-send-button">{ok} Einladungen senden</Button>
        </div>
      )}
    </Card>
  );
}

function Template({ admin }: { admin: boolean }) {
  const [v, setV] = useState<any>(null);
  useEffect(() => { api.get("/praxis/einladungen/vorlage").then((r) => setV(r.data)); }, []);
  if (!v) return null;
  const save = async () => { try { await api.put("/praxis/einladungen/vorlage", v); toast.success("Vorlage gespeichert."); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card><h2 className="mb-2">Vorlage der Einladungsmail</h2><p className="mb-3 text-sm text-text-muted">Platzhalter: {"{Fachpraxis}"}, {"{Absender}"}, {"{Datum}"}. Button und Abmelde-Link werden automatisch ergänzt.</p>
      <Field label="Betreff" htmlFor="vb"><Input id="vb" disabled={!admin} value={v.betreff} onChange={(e) => setV({ ...v, betreff: e.target.value })} data-testid="tpl-subject-input" /></Field>
      <Field label="Text" htmlFor="vt"><Textarea id="vt" rows={6} disabled={!admin} value={v.text} onChange={(e) => setV({ ...v, text: e.target.value })} data-testid="tpl-text-input" /></Field>
      <Checkbox checked={v.erinnerung_aktiv} onChange={(x) => admin && setV({ ...v, erinnerung_aktiv: x })} label="Automatische Erinnerung nach 7 Tagen (einmalig)" testid="tpl-reminder-checkbox" />
      {admin && <Button className="mt-3" onClick={save} data-testid="tpl-save-button">Vorlage speichern</Button>}
    </Card>
  );
}

function Overview({ list }: { list: any[] }) {
  const n = (s: string) => list.filter((e) => e.status === s).length;
  const total = list.filter((e) => e.status !== "ZURUECKGEZOGEN").length;
  const quote = total ? Math.round((100 * n("ANGENOMMEN")) / total) : 0;
  const top: Record<string, { name: string; gesendet: number; angenommen: number }> = {};
  list.filter((e) => e.absender_typ === "UEBERWEISER").forEach((e) => {
    const k = e.eingeladen_von || "Unbekannt";
    top[k] = top[k] || { name: k, gesendet: 0, angenommen: 0 };
    top[k].gesendet++; if (e.status === "ANGENOMMEN") top[k].angenommen++;
  });
  const ranking = Object.values(top).sort((a, b) => b.angenommen - a.angenommen || b.gesendet - a.gesendet).slice(0, 5);
  const tiles: [string, string | number, string][] = [["Gesendet", total, "sent"], ["Angenommen", n("ANGENOMMEN"), "accepted"], ["Offen", n("OFFEN"), "open"], ["Annahmequote", `${quote} %`, "rate"]];
  return (
    <div className="grid gap-5 lg:grid-cols-[2fr_1fr]" data-testid="invite-overview">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{tiles.map(([l, v, id]) => (
        <Card key={id} className="!p-4"><p className="text-sm text-text-muted">{l}</p><p className="text-2xl font-semibold" data-testid={`invite-kpi-${id}`}>{v}</p></Card>))}
        <p className="col-span-full text-sm text-text-muted">Abgelaufen: {n("ABGELAUFEN")} · Zurückgezogen: {n("ZURUECKGEZOGEN")}</p></div>
      <Card className="!p-4" data-testid="invite-top-recommenders"><h2 className="mb-2">Wer am meisten empfiehlt</h2>
        {ranking.length === 0 ? <p className="text-sm text-text-muted">Noch keine Empfehlungen durch Überweiser.</p> : (
          <ol className="space-y-2 text-sm">{ranking.map((r, i) => (
            <li key={r.name} className="flex justify-between gap-3 border-b border-border pb-2 last:border-0"><span className="break-words">{i + 1}. {r.name}</span>
              <span className="whitespace-nowrap font-semibold">{r.angenommen} / {r.gesendet} angenommen</span></li>))}</ol>)}</Card>
    </div>
  );
}

export default function PraxisInvites() {
  const me: any = useOutletContext();
  const [list, setList] = useState<any[] | null>(null);
  const load = () => api.get("/praxis/einladungen").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id: string, a: string) => { try { await api.post(`/praxis/einladungen/${id}/${a}`); toast.success("Erledigt."); load(); } catch (e) { toast.error(errMsg(e)); } };
  const card = async () => {
    const r = await api.get("/praxis/einladungen/karte.pdf", { responseType: "blob" });
    const u = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); const a = document.createElement("a"); a.href = u; a.download = "Einladungskarte.pdf"; a.click(); URL.revokeObjectURL(u);
  };
  return (
    <div className="kd-fade space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3"><h1>Überweiser einladen</h1>
        <Button variant="secondary" onClick={card} data-testid="invite-card-button"><Download className="h-4 w-4" aria-hidden />Einladungskarte (PDF, A6)</Button></div>
      {list && <Overview list={list} />}
      <div className="grid gap-5 lg:grid-cols-2"><Single reload={load} /><CsvUpload reload={load} /></div>
      <Card className="!p-0 overflow-x-auto">
        <h2 className="p-4">Einladungen</h2>
        {!list ? <Spinner /> : (
          <table className="w-full text-left text-sm" data-testid="invites-table">
            <thead className="border-b border-border bg-bg text-text-muted"><tr>{["Empfänger", "E-Mail", "Eingeladen von", "Datum", "Status", ""].map((h) => <th key={h} className="px-3 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>{list.map((e) => (
              <tr key={e.id} className="border-b border-border last:border-0" data-testid={`invite-row-${e.email}`}>
                <td className="px-3 py-2">{e.name || "–"}{e.praxis ? ` (${e.praxis})` : ""}</td><td className="px-3 py-2">{e.email}</td>
                <td className="px-3 py-2">{e.eingeladen_von}{e.absender_typ === "UEBERWEISER" ? " · Empfehlung" : ""}</td>
                <td className="px-3 py-2">{fmtDate(e.erstellt_am)}</td><td className="px-3 py-2">{ST[e.status]}</td>
                <td className="whitespace-nowrap px-3 py-2">{["OFFEN", "ABGELAUFEN"].includes(e.status) && <>
                  <button className="mr-2 inline-flex min-h-[44px] items-center gap-1 text-primary" onClick={() => act(e.id, "erneut")} data-testid={`invite-resend-${e.email}`}><RotateCw className="h-4 w-4" aria-hidden />Erneut senden</button>
                  {e.status === "OFFEN" && <button className="inline-flex min-h-[44px] items-center gap-1 text-danger" onClick={() => act(e.id, "zurueckziehen")} data-testid={`invite-revoke-${e.email}`}><XCircle className="h-4 w-4" aria-hidden />Zurückziehen</button>}
                </>}</td>
              </tr>))}</tbody>
          </table>
        )}
      </Card>
      <Template admin={me.benutzer.rolle === "ADMIN"} />
    </div>
  );
}
